import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDecision } from '@optimizely/react-sdk';
import { useRouter } from 'next/router';
import { openModal } from '@/localization/actions';
import RecipeQuantitySelector from '../../../coreComponents/buttons/RecipeQuantitySelector';
import fontStyles from '../../../common/fontStyles';
import colors from '../../../common/colors';
import { mobileStartBreakpoint } from '@/materialUi/theme';
import { onAddToOrderButtonSuccess } from '@/dataAnalytics/dataAnalyticsHelper';
import useAddToCart, { transformCCCartItemToLegacy } from '../../../cart/hooks/useAddToCart';
import { BuiltPizza } from '@/builders/pizza/dataTransformers/builderTypes';
import { Deal } from '@/builders/deals/slice/dealTypes';
import {
  cartErrorModalDetails,
  getDealErrorModalDetails,
  NOT_COMBINABLE_DEALS_TITLE,
  showDealNotinTimeWindowModal
} from '@/common/Modal/modalDetails';
import { useLineup } from '@/builders/deals/hooks/useLineup';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { getOrInitializeOptimizely } from '../../../../optimizely/optimizely';
import { getGlobalId } from '@/cart/helpers/getGlobalId';
import { dealSelectors } from '@/builders/deals/slice/deal.slice';
import { orderSelectors } from '@/clientCore/redux/selectors/orderSelectors';
import DealType from '@/builders/deals/DealTypes';
import {
  checkIsPastEndTime,
  checkIsPastIntervalStartTime,
  getTimerDetails
} from '@/common/countdownTimer/getTimerDetails';
import { localizationSelectors } from '@/localization/localizationSelectors';
import Routes from '@/router/routes';

export interface AddToCartButtonProps {
  item: BuiltPizza | Deal;
  onFinishedAdd: () => void;
  showQuantity: boolean;
  buttonText: string;
  onClickAnalytics?: () => void;
  onSuccessAddToCartAnalytics?: () => void;
  quantity?: number;
}

const useStyles = makeStyles((theme) => ({
  addToOrderContainerLegacy: {
    height: '40px',
    [theme.breakpoints.down(mobileStartBreakpoint)]: {
      height: '32px'
    }
  },
  addToOrderContainer: {
    display: 'flex',
    height: '32px',
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      height: '40px'
    }
  },
  addToOrderContainerAdjusted:{
    width: '100%',
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      width:'226px'
    }
  },
  addBtnLegacy: {
    ...fontStyles.primaryCtaLabel,
    'text-transform': 'uppercase',
    height: '32px',
    width: '88px',
    padding: '6px 0 7px 0',
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    border: `solid 1px ${colors.red}`,
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      width: '226px',
      height: '40px'
    }
  },
  addBtn: {
    ...fontStyles.primaryCtaLabel,
    'text-transform': 'uppercase',
    padding: '6px 0 7px 0',
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    border: `solid 1px ${colors.red}`,
    flex: 1
  },
  hasNoQuantityLegacy: {
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    width: '226px',
    [theme.breakpoints.down(mobileStartBreakpoint)]: {
      width: '100%'
    },
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      width: '226px'
    }
  },
  hasNoQuantity: {
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px'
  }
}));

const AddToCartButton = (props: AddToCartButtonProps): JSX.Element => {
  const {
    item,
    onFinishedAdd,
    showQuantity,
    buttonText,
    onClickAnalytics,
    onSuccessAddToCartAnalytics,
    quantity: initialQuantity = 1
  } = props;
  const [quantity, setQuantity] = useState<number>(initialQuantity);

  const classes = useStyles();
  const dispatch = useDispatch();
  const { addToCart } = useAddToCart();
  const [loading, setLoading] = useState<boolean>(false);
  const { isLineup } = useLineup();
  const analytics = useAnalytics();
  const hasFiredAnalyticsEvent = useRef(false);
  const dealData = useSelector(dealSelectors.selectDeal);
  const globalDealId = getGlobalId(dealData?.data?.id);
  const optimizelyInstance = getOrInitializeOptimizely();
  const cartItems = useSelector(orderSelectors.cartItems);
  const occasion = useSelector(localizationSelectors.occasion);
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const router = useRouter();
  const isDeal = 'data' in item;

  const [{ enabled: singleStepDealsQuantityAddToCartFixEnabled }] = useDecision(
    'bug-dtg-1681-single-step-deals-quantity-add-to-cart'
  );
  const [{ enabled: isSingleStepPizzaDealQuantityPickerEnabled, variationKey }] = useDecision(
    'fr-dtg-218-quantity-picker-single-step-pizza-deal'
  );
  const isSingleStepDealQtyPickerABTestOn =
    isSingleStepPizzaDealQuantityPickerEnabled && variationKey === 'control_qty_pick_pizza_deal_on';
  const [{ enabled: isAddToCartButtonRefactorEnabled }] = useDecision('fr-dtg-1699-quantity-picker-cta-placement');
  const [{ enabled: isNewDealTimeExpiresErrorEnabled }] = useDecision('fr-ims-599-fix-dtg-1778');
  const [{enabled: isButtonStyleAdjustmentEnabled}] = useDecision('fr-ims-225-button-style-adjustment');
  const [{ enabled: isStoreTimezoneAvailabilityEnabled }] = useDecision('fr-ims-614-crafted-flatzz-timezone-bug');

  const onAddToCartFinishedSuccessfully = () => {
    if (onSuccessAddToCartAnalytics) {
      onSuccessAddToCartAnalytics();
    } else {
      analytics.push(() => onAddToOrderButtonSuccess(item, isLineup));
    }

    optimizelyInstance?.track('AddToOrder', { dealId: globalDealId as string });

    onFinishedAdd();
    setLoading(false);
  };

  const onFailedAddToOrder = (err?: Error) => {
    let dealErrorModalDetails;

    if (err && err.message) {
      dealErrorModalDetails = getDealErrorModalDetails(NOT_COMBINABLE_DEALS_TITLE, err.message);
    }
    dispatch(openModal(dealErrorModalDetails || cartErrorModalDetails));
    setLoading(false);
  };

  const addToOrderAction = (eventKeyboardClick: boolean) => {
    setLoading(true);

    const cartItem =
      singleStepDealsQuantityAddToCartFixEnabled && isDeal
        ? { ...item, data: { ...(item as Deal).data, quantity } }
        : { ...item, quantity };
    
    const transformedCartItems = cartItems?.map(transformCCCartItemToLegacy);
    addToCart(cartItem, onAddToCartFinishedSuccessfully, onFailedAddToOrder, undefined, eventKeyboardClick, isLineup, transformedCartItems, () => {
      setLoading(false);
    });
  };

  const handleClick = (eventProps: React.MouseEvent<Element>) => {
    if (isNewDealTimeExpiresErrorEnabled && isDeal && item?.data?.type === DealType.SINGLE_STEP) {
      const {duration, intervalStartTime} = getTimerDetails(item?.data?.availability, occasion, storeDetails?.storeTimezone );
      const now = isStoreTimezoneAvailabilityEnabled ? new Date(new Date().toLocaleString('en-US', { timeZone: storeDetails?.storeTimezone })) : new Date();
      const isPastIntervalStartTime = checkIsPastIntervalStartTime(now, intervalStartTime);
      const isPastEndtime = Boolean(duration && intervalStartTime && checkIsPastEndTime(storeDetails?.storeTimezone, duration, intervalStartTime));
      if(isPastEndtime || !isPastIntervalStartTime) {
        const onClose = () => router.push(Routes.DEALS);
        dispatch(openModal(showDealNotinTimeWindowModal(onClose)));
        return;
      }
    }

    if (!loading) {
      onClickAnalytics?.();
      addToOrderAction(eventProps.detail === 0);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
  };

  useEffect(() => {
    if (isSingleStepDealQtyPickerABTestOn || hasFiredAnalyticsEvent.current) {
      if (variationKey === 'control_qty_pick_pizza_deal_on') {
        analytics.push(() => ({
          event: 'FFE-QuantityPicker',
          event_category: 'Feature Flag Experiment',
          beacon_id: '99.07.02',
          event_action: 'Treated_QuantityPicker'
        }));
      } else {
        analytics.push(() => ({
          event: 'FFE-QuantityPicker',
          event_category: 'Feature Flag Experiment',
          beacon_id: '99.08.02',
          event_action: 'Untreated_QuantityPicker'
        }));
      }
    }
  }, [isSingleStepDealQtyPickerABTestOn, analytics, variationKey]);

  if (isAddToCartButtonRefactorEnabled) {
    return (
      <Box className={`${classes.addToOrderContainer} ${isButtonStyleAdjustmentEnabled ? classes.addToOrderContainerAdjusted : ''}`}>
        {showQuantity && (
          <RecipeQuantitySelector isDeal={isDeal} onUpdateQuantity={handleQuantityChange} quantity={quantity} />
        )}
        <Button
          aria-label={loading ? 'Adding to cart...' : undefined}
          className={`${classes.addBtn} ${showQuantity ? '' : classes.hasNoQuantity}`}
          color="primary"
          data-testid="add-to-cart-button"
          onClick={handleClick}
          variant="contained"
        >
          {loading ? <CircularProgress size={24} /> : 'Add to cart'}
        </Button>
      </Box>
    );
  }

  return (
    <Grid item container wrap="nowrap" justifyContent="flex-end" className={classes.addToOrderContainerLegacy}>
      {showQuantity && (
        <RecipeQuantitySelector quantity={quantity} isDeal={isDeal} onUpdateQuantity={handleQuantityChange} />
      )}
      <Button
        data-testid="add-to-cart-button"
        className={`${classes.addBtnLegacy} ${showQuantity ? '' : classes.hasNoQuantityLegacy}`}
        onClick={handleClick}
        variant="contained"
        color="primary"
        aria-label={buttonText}
      >
        {loading ? <CircularProgress size={24} /> : buttonText}
      </Button>
    </Grid>
  );
};

export default AddToCartButton;
